// 3rd-party libraries
import * as MsalBrowser from '@azure/msal-browser';
import { push } from 'connected-react-router';
import { Formik } from 'formik';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Yup from 'yup';

// Material UI
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import FormHelperText from '@material-ui/core/FormHelperText';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import { updateSession } from 'global/actions/global-actions';
import axios from 'global/axios-instance';

// constants
import * as MsalConstants from 'msal-constants';
import UiConfig from 'constants/ui-config';

// static assets
import googleLogo from 'assets/google_logo.png';
import laerdalLogo from 'assets/laerdal_logo.png';
import jausLogo from 'assets/jaus_new_200.png';

const schema = Yup.object().shape({
    email: Yup.string()
        .required('Please enter your email')
        .email('Invalid email address'),
    password: Yup.string()
        .required('Please enter your password')
});

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        updateSession,
        push
    }, dispatch);
}

const styles = theme => ({
    container: {
        top: '50%',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: '350px'
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    heading: {
        textAlign: 'center'
    },
    textField: {
        margin: theme.spacing(1),
        width: 200
    },
    dense: {
        marginTop: 19
    },
    menu: {
        width: 200
    },
    button: {
        margin: theme.spacing(1)
    },
    sectionHeader: {
        textAlign: 'center',
        margin: '1rem 0 1rem 0',
        fontSize: 14
    },
    divider: {
        width: '100%',
        marginTop: '1rem'
    },
    logo: {
        width: '150px',
        height: '150px'
    },
    googleButton: {
        textTransform: 'none',
        color: '#FFFFFF',
        backgroundColor: '#4c8bf5',
        paddingLeft: '0.1rem',
        paddingTop: '0.12rem',
        paddingBottom: '0.12rem',
        marginBottom: '0.5rem'
    },
    laerdalButton: {
        textTransform: 'none',
        color: '#FFFFFF',
        backgroundColor: '#FF0000',
        paddingLeft: '0.12rem',
        paddingTop: '0.12rem',
        paddingBottom: '0.12rem'
    },
    image: {
        width: '35px',
        height: '33px',
        marginRight: '0.5rem'
    }
});

// PublicClientApplication is the recommended class for most SPAs. See:
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev
//      /lib/msal-browser/docs/v4-migration.md#migration-example
const msalInstance = new MsalBrowser.PublicClientApplication(MsalConstants.MSAL_CONFIG);
await msalInstance.initialize();

// Safari has super strict popup policies from its "Intelligent Tracking
// Prevention (ITP)" which blocks popups if it thinks the popups aren't from
// direct user interaction. ssoSilent() will fail in Safari, so skip it and
// force the user to enter credentials. See "Why does ssoSilent fail in Safari?"
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev
//      /lib/msal-browser/FAQ.md#why-does-ssosilent-fail-in-safari
const IS_SAFARI = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

@withStyles(styles)
@connect(null, mapDispatchToProps)
class Login extends React.PureComponent {
    state = {
        credentialsError: null,
        ssoError: null
    };

    static propTypes = {
        updateSession: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired,
        classes: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired
    };

    constructor(props) {
        super(props);
        this.doSubmit = this.doSubmit.bind(this);
        this.doGoogleLogin = this.doGoogleLogin.bind(this);
        this.doMicrosoftLogin = this.doMicrosoftLogin.bind(this);
    }

    doSubmit(values, { setSubmitting }) {
        if (!_.isEmpty(this.state.credentialsError)) {
            this.setState({
                credentialsError: null
            });
        }
        axios.post('/auth/challenge', values)
            .then(response => {
                this.props.updateSession({
                    token: response.data.token,
                    user: response.data.user,
                    refreshToken: response.data.refreshToken
                });
                setSubmitting(false);
                this.props.push('/');
            })
            .catch(err => {
                let credentialsError;
                if (_.isNil(err.response) || _.isNil(err.response.status)) {
                    credentialsError = 'A network error occurred.';
                } else if (err.response.data?.message === 'INVALID_CREDENTIALS'
                    || err.response.data?.message === 'ACCOUNT_DEACTIVATED') {
                    credentialsError = err.response.data.meta.detail;
                } else {
                    credentialsError = 'An unknown error occurred.';
                }
                this.setState({ credentialsError });
                setSubmitting(false);
            });
    }

    async doGoogleLogin() {
        // There's a miniscule chance this isn't loaded by the time the button's clicked. Not
        // if it happens we can move to injecting the gapi code and watching the onLoad function
        window.gapi.auth2.authorize({
            client_id: UiConfig.GAPI_CLIENT_ID,
            scope: 'profile email',
            response_type: 'code',
            hosted_domain: 'blinemedical.com'
        }, async (authorizationResponse) => {
            if (!authorizationResponse.code) {
                // eslint-disable-next-line no-console
                console.error('Code is missing from google auth request. Seems to happen in some very weird situations '
                    + 'due to caching: https://stackoverflow.com/a/45761277');
            }
            const responseData = await axios.post('/auth/google', {
                code: authorizationResponse.code
            });

            const { token, user, refreshToken } = responseData.data;

            this.props.updateSession({
                token,
                user,
                refreshToken
            });

            if (_.isNil(this.props.location.state) || _.isNil(this.props.location.state.from)) {
                // Saw this happen once, not sure why
                // eslint-disable-next-line no-console
                console.warn('state.from is empty');
                return this.props.push('/');
            }

            this.props.push(this.props.location.state.from);
        });
    }

    // Microsoft SSO login flow:
    // 1. acquireTokenSilent() attempts to find valid token in MSAL cache.
    // 2. ssoSilent() if no token found, attempt to find token from IdP session.
    // 3. loginPopup() if still no token found. User has to enter credentials.
    // 4. POST /auth/microsoft to complete the validation
    async doMicrosoftLogin() {
        // Reset the SSO error message on each login attempt.
        // There is no need to worry about the credentialsError message;
        // Users will not have that feature on production.
        if (!_.isEmpty(this.state.ssoError)) {
            this.setState({ ssoError: null });
        }

        const response = await this.tryAcquireTokenSilent()
            ?? await this.trySsoSilent()
            ?? await this.tryLoginPopup();

        if (!_.isNil(response)) {
            await this.useTokenResponse(response);
        }
    }

    tryAcquireTokenSilent = async () => {
        let response;

        const accounts = msalInstance.getAllAccounts();
        if (!_.isEmpty(accounts)) {
            try {
                // acquireTokenSilent() does a silent login (user does not have
                // to enter SSO credentials) that checks whether a valid token
                // exists in the MSAL cache (sessionStorage), using the key
                // msal.token.keys.<CLIENT_ID>. See MsalHelpers.msalConfig for
                // the client ID. See docs: https://github.com/AzureAD
                // /microsoft-authentication-library-for-js/blob/dev/lib
                // /msal-browser/docs/acquire-token.md#acquiring-an-access-token
                response = await msalInstance.acquireTokenSilent({
                    scopes: ['User.Read'],
                    account: accounts[0]
                });
            } catch (acquireTokenSilentError) {
                let acquireTokenSilentErrorMessage;
                const { errorCode } = acquireTokenSilentError;
                if (acquireTokenSilentError instanceof MsalBrowser.InteractionRequiredAuthError) {
                    acquireTokenSilentErrorMessage = `acquireTokenSilent() errored with InteractionRequiredAuthError `
                        + `(${errorCode})`;
                    const errorMessage = MsalConstants.INTERACTION_REQUIRED_AUTH_ERROR_MESSAGES[errorCode];
                    if (!_.isNil(errorMessage)) {
                        acquireTokenSilentErrorMessage += `: ${errorMessage}.`;
                    }
                } else {
                    const fallbackCode = errorCode || acquireTokenSilentError.message;
                    acquireTokenSilentErrorMessage = `acquireTokenSilent() errored with an unknown error `
                        + `(${fallbackCode})`;
                    window.console.warn(acquireTokenSilentError);
                }
                window.console.warn(acquireTokenSilentErrorMessage);
            }
        }
        return response;
    };

    trySsoSilent = async () => {
        let response;

        // See IS_SAFARI notes above; it explains the reason for this check.
        if (!IS_SAFARI) {
            try {
                // ssoSilent() does a silent login (user does not have to enter
                // SSO credentials) if there is a IdP session cookie. See docs:
                // https://github.com/AzureAD
                // /microsoft-authentication-library-for-js/blob/dev/lib
                // /msal-browser/docs/login-user.md#silent-login-with-ssosilent
                response = await msalInstance.ssoSilent({ scopes: ['User.Read']});
            } catch (ssoSilentError) {
                let ssoSilentErrorMessage;
                const { errorCode } = ssoSilentError;
                if (ssoSilentError instanceof MsalBrowser.InteractionRequiredAuthError) {
                    ssoSilentErrorMessage = `ssoSilent() errored with InteractionRequiredAuthError (${errorCode})`;
                    const errorMessage = MsalConstants.INTERACTION_REQUIRED_AUTH_ERROR_MESSAGES[errorCode];
                    if (!_.isNil(errorMessage)) {
                        ssoSilentErrorMessage += `: ${errorMessage}.`;
                    }
                } else {
                    const fallbackCode = errorCode || ssoSilentError.message;
                    ssoSilentErrorMessage = `ssoSilent() errored with an unknown error (${fallbackCode})`;
                    window.console.warn(ssoSilentError);
                }
                // @todo: AUS-357 sending logs to console is acceptable for now,
                //        until we send logs to Datadog RUM.
                window.console.warn(ssoSilentErrorMessage);
            }
        }
        return response;
    };

    tryLoginPopup = async () => {
        let response;
        try {
            response = await msalInstance.loginPopup({
                scopes: ['User.Read'],
                overrideInteractionInProgress: true
            });
        } catch (loginPopupError) {
            let loginPopupErrorMessage;
            const { errorCode } = loginPopupError;
            // Checking for instance of InteractionRequiredAuthError, is likely
            // unnecessary, since loginPopup() should have caught those already.
            // Also not included: ServerAuthError - revisit if it's determined
            // that ServerAuthError happens frequently.
            if (loginPopupError instanceof MsalBrowser.BrowserAuthError) {
                loginPopupErrorMessage = `loginPopup() errored with BrowserAuthError (${errorCode})`;
                const errorMessage = MsalConstants.BROWSER_AUTH_ERROR_MESSAGES[errorCode];
                if (!_.isNil(errorMessage)) {
                    loginPopupErrorMessage += `: ${errorMessage}.`;
                }
            } else if (loginPopupError instanceof MsalBrowser.ClientAuthError) {
                loginPopupErrorMessage = `loginPopup() errored with ClientAuthError (${errorCode}) (possibly related `
                    + `network connectivity): ${loginPopupError.message}`;
            } else {
                const fallbackCode = errorCode || loginPopupError.message;
                loginPopupErrorMessage = `loginPopup() errored with an unknown error (${fallbackCode}): `
                    + loginPopupError.message;
                window.console.error(fallbackCode);
            }
            // @todo: AUS-357 sending logs to console and the login form is
            //        acceptable for now, until we send logs to Datadog RUM.
            window.console.error(loginPopupError);
            this.setState({
                ssoError: loginPopupErrorMessage
            });
            return;
        }
        return response;
    };

    useTokenResponse = async (response) => {
        try {
            const authResponse = await axios.post('/auth/microsoft', {
                accessToken: response.accessToken
            });
            const { token, user, refreshToken } = authResponse.data;

            this.props.updateSession({
                token,
                user,
                refreshToken
            });

            if (_.isNil(this.props.location.state) || _.isNil(this.props.location.state.from)) {
                return this.props.push('/');
            }

            this.props.push(this.props.location.state.from);
        } catch (err) {
            window.console.error('useTokenResponse() error:', err);
            this.setState({ ssoError: 'Microsoft SSO login failed. Please try again.' });
        }
    };

    render() {
        const { classes } = this.props;
        const {
            credentialsError,
            ssoError
        } = this.state;
        return (
            <Formik
                initialValues={{ email: '', password: '' }}
                validationSchema={schema}
                onSubmit={this.doSubmit}>
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting
                    /* and other goodies */
                }) => (
                    <Paper className={classes.container}>
                        <div><img className={classes.logo} src={jausLogo} alt='jaus' /></div>
                        <Typography variant='h6' className={classes.sectionHeader}>
                            Sign in with your Laerdal or B-Line Medical e-mail account
                        </Typography>
                        <Button className={classes.googleButton}
                            variant='contained'
                            onClick={this.doGoogleLogin}>
                            <img src={googleLogo} className={classes.image} alt='Sign in with Google' />
                            Sign in with Google
                        </Button>
                        <Button className={classes.laerdalButton}
                            variant='contained'
                            onClick={this.doMicrosoftLogin}>
                            <img src={laerdalLogo} className={classes.image} alt='Sign in with Laerdal' />
                            Sign in with Laerdal
                        </Button>
                        {!_.isEmpty(ssoError) &&
                            <FormHelperText error={true}>{ssoError}</FormHelperText>
                        }
                        {(UiConfig.ENVIRONMENT === 'local' || UiConfig.ENVIRONMENT === 'development') &&
                            <React.Fragment>
                                <Divider className={classes.divider} />
                                <form onSubmit={handleSubmit} className={classes.form}>
                                    <TextField
                                        error={errors.email && touched.email}
                                        label='Email'
                                        type='email'
                                        name='email'
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.email}
                                        className={classes.textField}
                                        helperText={errors.email && touched.email && errors.email}
                                        autoComplete='email'
                                        inputProps={{
                                            'data-test-id': 'login-email-input'
                                        }} />
                                    <TextField
                                        error={errors.password && touched.password}
                                        label='Password'
                                        type='password'
                                        name='password'
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.password}
                                        className={classes.textField}
                                        helperText={errors.password && touched.password && errors.password}
                                        autoComplete='current-password'
                                        inputProps={{
                                            'data-test-id': 'login-password-input'
                                        }} />
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        className={classes.button}
                                        disabled={isSubmitting}
                                        type='submit'
                                        data-test-id='login-submit-button'>
                                        Submit
                                    </Button>
                                    {!_.isEmpty(credentialsError) &&
                                        <FormHelperText error={true}>{credentialsError}</FormHelperText>
                                    }
                                </form>
                            </React.Fragment>
                        }
                    </Paper>
                )}
            </Formik>
        );
    }
}

export default Login;
